// app/middleware/preserve-request-route.global.ts

/**
 * This middleware addresses an issue where a caching layer (like Akamai)
 * might serve an initial HTML response with a URL that differs from the
 * actual URL in the user's browser. This can cause a hydration mismatch
 * on the client, where Vue Router's initial route (`to`) doesn't align
 * with the browser's address bar.
 *
 * This middleware runs on the client-side for specific
 * product-related routes. It compares the browser's URL with the route
 * provided by Nuxt. If they don't match, it aborts the initial navigation
 * and replaces it with a navigation to the correct browser URL.
 */
const VALID_ROUTES = new Set(['product', 'productWithoutName']);
const MAX_REDIRECTS = 1;
let redirectCount = 0;

function normalizeSearchParams(search) {
  const params = new URLSearchParams(search);

  const normalized = [...params.entries()]
    .map(([key, value]) => {
      const decodedKey = decodeURIComponent(key.replace(/\+/g, ' '));
      const decodedValue = decodeURIComponent(value.replace(/\+/g, ' '));

      return [encodeURIComponent(decodedKey), encodeURIComponent(decodedValue)];
    })
    .sort(([a], [b]) => a.localeCompare(b));

  return normalized;
}

function compareSearchParams(akamaiSearch, browserSearch) {
  const a = normalizeSearchParams(akamaiSearch);
  const b = normalizeSearchParams(browserSearch);

  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    if (a[i][0] !== b[i][0] || a[i][1] !== b[i][1]) {
      return false;
    }
  }

  return true;
}

export default defineNuxtRouteMiddleware((to) => {
  if (!import.meta.client || !to?.name || !VALID_ROUTES.has(to.name as string)) return;

  try {
    const browserURL = new URL(window.location.href);
    const { pathname, search, hash, searchParams } = browserURL;
    const browserQuery = Object.fromEntries(searchParams);

    const toURL = new URL(to.fullPath, window.location.origin);

    const urlsMatch = pathname === toURL.pathname && compareSearchParams(toURL.search, search);

    if (!urlsMatch) {
      if (redirectCount > MAX_REDIRECTS) return;
      redirectCount++;

      // If url from cache and url in browser don't match
      // abort current navigation and replace with real browser URL
      useNuxtApp()?.$router?.replace({
        path: pathname,
        query: browserQuery,
        hash,
      });
    } else {
      redirectCount = 0;
    }
  } catch (error) {
    console.error('Preserver request route Middleware error:', error);
  }
});
